import { useTheme } from '@mui/material';
import { type Theme } from '@mui/material';
import { type SystemStyleObject } from '@mui/system';
import { type MovieStatus, type TvStatus, type VideoType } from '@tmdb-graphql-api/schema/types';
import { type JSX, useState } from 'react';
import { MoreOptionButton } from '#components/MoreOptionsButton/index.tsx';
import { type SectionType } from '#enums/mediaTypes.ts';
import { mapSectionTypeToScreenFormatType, mapSectionTypeToScreenType } from '#helpers/enumMappers.ts';
import { isMovieOrTv } from '#helpers/isMovieOrTv.ts';
import { isMovieTvOrSeason } from '#helpers/isMovieTvOrSeason.ts';
import { isPerson } from '#helpers/isPerson.ts';
import { type ActivePreview, type ActiveVideo, type PathIds } from '#types.ts';
import { Query as CertificationIconQuery } from '../CertificationIcon/index.ts';
import { Ribbon } from '../Ribbon/index.tsx';
import { VideoButton } from '../VideoButton/index.tsx';
import { CertificationIconContainer, Container, Image, OuterContainer } from './styled.ts';

export type PosterImageProps = {
  action?: 'preview' | 'video';
  id: string | number;
  pathIds?: PathIds;
  renderAdditionalOptionItems?: () => JSX.Element | undefined;
  setActivePreview?: (item: ActivePreview) => void;
  setActiveVideo?: (video: ActiveVideo) => void;
  showCertification?: boolean;
  showMoreOptions?: boolean;
  src: string;
  status?: MovieStatus | TvStatus;
  sx?: SystemStyleObject<Theme>;
  title: string;
  type: SectionType;
  videoCount?: number;
  videoTypes?: VideoType[];
};

export const PosterImage = ({
  action,
  id,
  pathIds,
  renderAdditionalOptionItems,
  setActivePreview,
  setActiveVideo,
  showCertification,
  showMoreOptions,
  src,
  status,
  sx,
  title,
  type,
  videoCount,
  videoTypes,
}: PosterImageProps) => {
  const theme = useTheme();

  const [styles, setStyles] = useState({
    backgroundColor: theme.palette.grey[300],
    borderColor: theme.palette.grey[300],
  });

  return (
    <OuterContainer sx={sx}>
      <Container sx={{ backgroundColor: styles.backgroundColor }}>
        <div style={{ position: 'relative' }}>
          <picture>
            <Image
              alt={title}
              loading="lazy"
              onLoad={() => {
                setStyles({ backgroundColor: 'transparent', borderColor: theme.palette.grey[900] });
              }}
              src={src}
              sx={{ borderColor: styles.borderColor }}
            />
          </picture>
          {isMovieOrTv(type) && showCertification ? (
            <CertificationIconQuery id={id} type={mapSectionTypeToScreenType(type)}>
              {certificationIcon => <CertificationIconContainer>{certificationIcon}</CertificationIconContainer>}
            </CertificationIconQuery>
          ) : undefined}
        </div>
      </Container>
      {isMovieTvOrSeason(type) ? (
        <Ribbon id={id} status={status} type={mapSectionTypeToScreenFormatType(type)} />
      ) : undefined}
      {isMovieOrTv(type) && showMoreOptions && status ? (
        <MoreOptionButton
          dropShadow
          // @ts-expect-error move and tv ids are always numbers
          id={id}
          renderAdditionalOptionItems={renderAdditionalOptionItems}
          status={status}
          sx={{
            color: theme.mainTextSecondary,
            position: 'absolute',
            right: theme.spacing(0.5),
            top: theme.spacing(0.5),
          }}
          type={mapSectionTypeToScreenType(type)}
        />
      ) : undefined}
      {!isPerson(type) && setActiveVideo ? (
        <VideoButton
          action={action}
          id={id}
          pathIds={pathIds}
          setActivePreview={setActivePreview}
          setActiveVideo={setActiveVideo}
          title={title}
          type={mapSectionTypeToScreenFormatType(type)}
          videoCount={videoCount}
          videoTypes={videoTypes}
        />
      ) : undefined}
    </OuterContainer>
  );
};
